"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useWaveActivityLogs } from "@/hooks/useWaveActivityLogs";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { SingleWaveDropLog } from "./SingleWaveDropLog";
interface SingleWaveDropLogsProps {
  readonly drop: ApiDrop;
}
export const SingleWaveDropLogs: React.FC<SingleWaveDropLogsProps> = t0 => {
  const $ = _c(40);
  const {
    drop
  } = t0;
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const {
    connectedProfile
  } = useAuth();
  const t1 = connectedProfile?.handle ?? undefined;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["DROP_VOTE_EDIT"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== drop.id || $[2] !== drop.wave.id || $[3] !== isActivityOpen || $[4] !== t1) {
    t3 = {
      waveId: drop.wave.id,
      connectedProfileHandle: t1,
      reverse: false,
      dropId: drop.id,
      logTypes: t2,
      enabled: isActivityOpen
    };
    $[1] = drop.id;
    $[2] = drop.wave.id;
    $[3] = isActivityOpen;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    logs,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading
  } = useWaveActivityLogs(t3);
  let t4;
  if ($[6] !== fetchNextPage || $[7] !== hasNextPage || $[8] !== isFetchingNextPage || $[9] !== isLoading) {
    t4 = () => {
      if (hasNextPage && !isLoading && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[6] = fetchNextPage;
    $[7] = hasNextPage;
    $[8] = isFetchingNextPage;
    $[9] = isLoading;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const intersectionElementRef = useIntersectionObserver(t4);
  let t5;
  if ($[11] !== isActivityOpen) {
    t5 = () => setIsActivityOpen(!isActivityOpen);
    $[11] = isActivityOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = `tw-flex tw-w-full tw-items-center tw-justify-between tw-border-0 tw-px-4 tw-py-4 tw-text-left tw-transition-colors tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-900 ${isActivityOpen ? "tw-bg-iron-800" : "tw-bg-iron-950"}`;
  const t7 = `tw-text-sm tw-font-medium ${isActivityOpen ? "tw-text-iron-300" : "tw-text-iron-400"}`;
  let t8;
  if ($[13] !== t7) {
    t8 = <span className={t7}>Activity log</span>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = isActivityOpen ? 180 : 0;
  let t10;
  if ($[15] !== t9) {
    t10 = {
      rotate: t9
    };
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.3
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = `tw-h-4 tw-w-4 tw-flex-shrink-0 ${isActivityOpen ? "tw-text-iron-400" : "tw-text-iron-600"}`;
  let t13;
  if ($[18] !== t12) {
    t13 = <ChevronDownIcon className={t12} />;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t10 || $[21] !== t13) {
    t14 = <motion.div animate={t10} transition={t11}>{t13}</motion.div>;
    $[20] = t10;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t5 || $[25] !== t6 || $[26] !== t8) {
    t15 = <button onClick={t5} className={t6}>{t8}{t14}</button>;
    $[23] = t14;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== drop.wave.voting_credit_type || $[29] !== intersectionElementRef || $[30] !== isActivityOpen || $[31] !== isFetchingNextPage || $[32] !== isLoading || $[33] !== logs) {
    t16 = isActivityOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="tw-overflow-hidden"><div className="tw-max-h-[19.75rem] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300">{logs.length > 0 || isLoading ? <div className="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">{logs.map(log => <SingleWaveDropLog key={log.id} log={log} creditType={drop.wave.voting_credit_type} />)}</div> : <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-4 tw-py-6 tw-text-iron-400"><div className="tw-group tw-relative"><div className="tw-absolute tw-inset-0 tw-animate-[spin_4s_linear_infinite] tw-rounded-full tw-bg-gradient-to-br tw-from-primary-400/20 tw-via-indigo-500/10 tw-to-iron-800/10 group-hover:tw-from-primary-400/30" /><div className="tw-absolute tw-inset-0 tw-animate-[spin_5s_linear_infinite] tw-rounded-full tw-bg-gradient-to-tr tw-from-iron-800/10 tw-via-indigo-500/10 tw-to-primary-400/20 group-hover:tw-to-primary-400/30" /><div className="tw-bg-gradient-radial tw-absolute tw-inset-0 tw-animate-pulse tw-from-primary-300/5 tw-to-transparent" /><svg className="tw-relative tw-size-8 tw-flex-shrink-0 tw-text-white/60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1" stroke="currentColor" aria-hidden="true"><path d="M22.7 13.5L20.7005 11.5L18.7 13.5M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.3019 3 18.1885 4.77814 19.7545 7.42909M12 7V12L15 14" strokeLinecap="round" strokeLinejoin="round" /></svg></div><div className="tw-flex tw-flex-col tw-items-center tw-gap-y-2"><span className="tw-bg-gradient-to-br tw-from-iron-200 tw-via-iron-300 tw-to-iron-400 tw-bg-clip-text tw-text-base tw-font-semibold tw-tracking-tight tw-text-transparent">Be the First to Make a Vote</span><p className="tw-mb-0 tw-max-w-64 tw-text-center tw-text-sm tw-text-iron-500">Vote on this drop to see activity updates appear here in real-time.</p></div></div>}{isFetchingNextPage && <div className="tw-h-0.5 tw-w-full tw-overflow-hidden tw-bg-iron-800"><div className="tw-h-full tw-w-full tw-animate-loading-bar tw-bg-indigo-400" /></div>}<div ref={intersectionElementRef} /></div></motion.div>;
    $[28] = drop.wave.voting_credit_type;
    $[29] = intersectionElementRef;
    $[30] = isActivityOpen;
    $[31] = isFetchingNextPage;
    $[32] = isLoading;
    $[33] = logs;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16) {
    t17 = <AnimatePresence>{t16}</AnimatePresence>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t15 || $[38] !== t17) {
    t18 = <div>{t15}{t17}</div>;
    $[37] = t15;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  return t18;
};